import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  getUserSubscription,
  getMonthlyUsage,
  canCreateProject
} from '@/lib/firebase/services/subscription';
import { SubscriptionTier, UserSubscription, MonthlyUsage } from '@/lib/firebase/types/subscription';

interface UseSubscriptionResult {
  subscription: UserSubscription | null;
  usage: MonthlyUsage | null;
  canCreate: boolean;
  createReason?: string;
  remainingProjects: number | 'unlimited';
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useSubscription(): UseSubscriptionResult {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usage, setUsage] = useState<MonthlyUsage | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [createReason, setCreateReason] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscriptionData = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 구독 정보 가져오기
      const subscriptionData = await getUserSubscription(user.uid);
      setSubscription(subscriptionData);

      // 월별 사용량 가져오기
      const usageData = await getMonthlyUsage(user.uid);
      setUsage(usageData);

      // 프로젝트 생성 가능 여부 확인
      const createPermission = await canCreateProject(user.uid);
      setCanCreate(createPermission.canCreate);
      setCreateReason(createPermission.reason);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, [user]);

  const remainingProjects = (() => {
    if (!subscription || !usage) return 0;
    const limits = require('@/lib/firebase/types/subscription').SUBSCRIPTION_LIMITS[subscription.tier];

    if (limits.monthlyProjects === -1) return 'unlimited';
    return Math.max(0, limits.monthlyProjects - usage.projectsCreated);
  })();

  return {
    subscription,
    usage,
    canCreate,
    createReason,
    remainingProjects,
    loading,
    error,
    refresh: fetchSubscriptionData
  };
}

// 구독 등급별 권한 확인 Hook
export function useSubscriptionFeature(feature: 'ultraQuality' | 'worldScene' | 'autoColorCorrection') {
  const { subscription } = useSubscription();
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (!subscription) {
      setIsAvailable(false);
      return;
    }

    const limits = require('@/lib/firebase/types/subscription').SUBSCRIPTION_LIMITS[subscription.tier];

    switch (feature) {
      case 'ultraQuality':
        setIsAvailable(limits.qualityOptions.ultra);
        break;
      case 'worldScene':
        setIsAvailable(limits.sceneSizeOptions.world);
        break;
      case 'autoColorCorrection':
        setIsAvailable(limits.autoColorCorrection);
        break;
    }
  }, [subscription, feature]);

  return isAvailable;
}